import { DashboardContainerHeader } from 'app/layout/dashboardLayout/BoxLayout';
import { IDirectoryNode } from 'app/services/documents';
import {
  CancelAssigneeIcon,
  CommentIcon,
  DownloadIcon,
  EditIcon,
  FileIcon,
  FolderSolidIcon,
  ViewIcon,
} from 'assets/icons';
import Button from 'Components/Buttons/Button';
import { USDate } from 'Components/Dates/USDate';
import OverlayWindow, { WindowSize } from 'Components/OverlayWindow';
import { useCurrentPopup } from 'Components/Popups';
import { saveAs } from 'file-saver';
import { Fragment, ReactNode, useState } from 'react';
import { getUserRole, useAuth } from 'utils/AuthProvider';
import { BulkDownloadModal, useBulkDownloadModal } from './BulkDownloadModal';
import DocumentNotes from './DocumentNotes';
import UpdateNodeForm from './UpdateNodeForm';

export function useHavePermissions() {
  const { isClient, userId, isAdmin } = useAuth();
  /* 
    Admin: any document/folder
    Accountant: only documents/folders that are created/uploaded by AOA users (accountants/admins)
    Client: only documents/folders that are created/uploaded by him
  */
  return (node?: IDirectoryNode) => {
    const creator = getUserRole(node?.creator?.roles || []);

    return isClient
      ? creator.isClient
      : isAdmin || creator.isAccountant || creator.isAdmin;
  };
}

export function DirectoryNodeDetailsModal() {
  const { contextData: node, close } = useCurrentPopup<IDirectoryNode>();
  const BulkModal = useBulkDownloadModal();

  const [view, setView] = useState<view>('info');
  const canEdit = useHavePermissions()(node);

  if (!node) return <></>;

  const data = [
    ['Name', node.title],
    ['Description', node.description],
    ['Created By', `${node.creator?.given_name} ${node.creator?.family_name}`],
    ['Created At', <USDate date={node.created_at} />],
    ['Document Type', node.doctype],
    [
      'Client',
      node.clients?.length &&
        (node.clients || [])
          .map((c) => `${c.given_name} ${c.family_name}`)
          .join(`\n`),
    ],
    ['Business', node.business?.name],
    ['Task', node.task?.title],
    ['Tax Return', node.projects?.map((p) => p.title).join(`\n`)],
    ['Content type', node.file_content_type],
  ].filter(([key, value]) => !!value) as [string, ReactNode][];

  return (
    <OverlayWindow
      size={WindowSize.lg + ' min-h-[40rem]'}
      paddingSize={'px-0 py-2'}
    >
      <DashboardContainerHeader
        title={
          <span className="flex gap-2 items-center">
            {node.object_type === 'folder' && (
              <FolderSolidIcon className="w-5 h-5 mr-3 shrink-0" />
            )}
            {node.object_type === 'document' && (
              <FileIcon className="w-5 h-5 mr-3 shrink-0" />
            )}
            <span className="line-clamp-1 break-all text-ellipsis">
              {node.title}
            </span>
          </span>
        }
      >
        <div className="flex items-center gap-2">
          <NavButton view="info" setView={setView} current={view}>
            <ViewIcon className="w-5 h-5" />
          </NavButton>

          {canEdit && (
            <NavButton view="edit" setView={setView} current={view}>
              <EditIcon className="w-5 h-5 " />
            </NavButton>
          )}
          {node.object_type === 'document' && (
            <>
              <NavButton view="comments" setView={setView} current={view}>
                <CommentIcon className="w-5 h-5" />
              </NavButton>

              {node.file_url && (
                <Button
                  className="border-none"
                  onClick={() =>
                    saveAs(
                      process.env.REACT_APP_URL + node.file_url,
                      node.title,
                    )
                  }
                >
                  <DownloadIcon className="w-5 h-5" />
                </Button>
              )}
            </>
          )}

          {node.object_type === 'folder' && (
            <>
              <Button
                className="border-none"
                onClick={() =>
                  BulkModal.openWithContext({
                    data: { id: node.id, title: node.title },
                  })
                }
              >
                <DownloadIcon className="w-5 h-5" />
              </Button>
              <BulkModal.Component>
                <BulkDownloadModal />
              </BulkModal.Component>
            </>
          )}

          <Button className="border-none" onClick={close}>
            <CancelAssigneeIcon className="w-4 h-4" />
          </Button>
        </div>
      </DashboardContainerHeader>

      {view === 'info' && (
        <div className="grid grid-cols-3">
          {data.map(([label, value]) => (
            <Fragment key={label}>
              <div className="col-span-1 border-b border-gray-100 capitalize text-gray-700 text-sm font-semibold p-3">
                {label}
              </div>
              <div className="col-span-2 border-b whitespace-pre-wrap border-gray-100 text-gray-dark capitalize font-semibold text-sm p-3">
                {value}
              </div>
            </Fragment>
          ))}
        </div>
      )}
      {view === 'edit' && <UpdateNodeForm value={node} />}

      {view === 'comments' && <DocumentNotes documentId={node.id} />}
    </OverlayWindow>
  );
}

type view = 'info' | 'comments' | 'edit';
function NavButton({
  view,
  setView,
  current,
  children,
}: {
  current: view;
  view: view;
  setView: (v: view) => void;
  children: ReactNode;
}) {
  return (
    <Button
      className={`border-none ${current === view ? 'shadow' : ''}`}
      theme={current === view ? 'green-outline' : 'gray-outline'}
      onClick={() => setView(view)}
    >
      {children}
    </Button>
  );
}
